import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '@/auth/useAuth';
import { ApiError } from '@/api/client';
import { addNoticeRecipient, listNoticeRecipients, removeNoticeRecipient } from '@/api/hardwareRequests';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { formatDate } from '@/lib/dateFormat';
import type { NoticeRecipient } from '@/types/oscar';

/**
 * Who is emailed when an approved request needs a purchase.
 *
 * <p>Anthony, 28/10/2026: <i>"email asset managers when a purchase is needed."</i>
 * The list is edited here, not in an app setting - an app setting is a deployment,
 * not configuration (his words on the standing project, 02/10/2026).</p>
 *
 * ⛔ EMPTY MEANS NOBODY IS EMAILED, and the panel says so in words: the list starts
 * empty, and an empty list otherwise looks exactly like one that works.
 *
 * ⛔ COMPANY ADDRESSES ONLY. The notice names an employee, so the panel says which
 * domain before anybody types, and shows the server's refusal if they try another.
 *
 * ⛔ READ BY APPROVERS, CHANGED BY ADMINS. A non-admin sees who is told and no
 * controls.
 */
export function PurchaseNoticePanel({ isAdmin }: { isAdmin: boolean }) {
  const { canApproveHardware } = useAuth();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<NoticeRecipient | null>(null);

  const list = useQuery({
    queryKey: ['hardware-notification-recipients'],
    queryFn: ({ signal }) => listNoticeRecipients(signal),
    enabled: canApproveHardware,
    retry: false,
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['hardware-notification-recipients'] });

  const add = useMutation({
    mutationFn: () => addNoticeRecipient(email.trim()),
    onSuccess: () => {
      setEmail('');
      setError(null);
      void refresh();
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : 'Could not add that person.'),
  });

  const remove = useMutation({
    mutationFn: (p: NoticeRecipient) => removeNoticeRecipient(p.RecipientId),
    onSuccess: () => {
      setRemoving(null);
      setError(null);
      void refresh();
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : 'Could not take that person off.'),
  });

  // ⛔ Nothing at all for anybody who cannot decide a request.
  if (!canApproveHardware) return null;

  const people = list.data?.Recipients ?? [];
  const domain = list.data?.Domain ?? '@onsite.com.au';

  return (
    <section className="mb-4 rounded-lg border border-slate-200 bg-white p-4 sm:p-5" aria-labelledby="purchase-notices-heading">
      <h2 id="purchase-notices-heading" className="font-heading text-base font-bold text-navy">
        Purchase notices
      </h2>
      <p className="mt-1 text-sm text-darkGrey/80">
        These people are emailed when an approved request has something nobody holds stock of, so it has to
        be bought. Only {domain} addresses can be added.
      </p>

      {list.isLoading && <p className="mt-2 text-sm text-darkGrey/70">Loading the list…</p>}

      {list.isError && (
        <p role="alert" className="mt-2 text-sm text-red">
          Could not read the list. If this keeps happening, contact Onsite IT Support.
        </p>
      )}

      {list.data && people.length === 0 && (
        <p className="mt-2 flex items-start gap-2 rounded border border-orange/30 bg-orange/5 px-3 py-2 text-sm text-darkGrey">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange" aria-hidden />
          <span>
            <strong>Nobody is on the list</strong>, so no purchase emails are sent.
          </span>
        </p>
      )}

      {people.length > 0 && (
        <ul className="mt-2 divide-y divide-border rounded border border-border">
          {people.map((p) => (
            <li key={p.RecipientId} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
              <span className="text-darkGrey">
                <span className="font-semibold text-navy">{p.Email}</span>
                <span className="ml-3 text-xs text-darkGrey/60">
                  Added by {p.AddedBy} on {formatDate(p.AddedAtUtc)}
                </span>
              </span>
              {isAdmin && (
                <button
                  type="button"
                  aria-label={`Take ${p.Email} off the purchase notice list`}
                  className="text-xs font-semibold text-navy hover:underline"
                  onClick={() => {
                    setError(null);
                    setRemoving(p);
                  }}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {isAdmin && (
        <form
          className="mt-3 flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (email.trim()) add.mutate();
          }}
        >
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
              Add a person
            </span>
            <input
              type="email"
              className="w-72 max-w-full rounded border border-border px-2 py-1.5 text-sm"
              value={email}
              maxLength={256}
              placeholder={`name${domain}`}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <button
            type="submit"
            disabled={email.trim().length === 0 || add.isPending}
            className="rounded bg-navy px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {add.isPending ? 'Adding…' : 'Add'}
          </button>
        </form>
      )}

      {error && !removing && (
        <p role="alert" className="mt-2 rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-darkGrey">
          {error}
        </p>
      )}

      <AlertDialog
        open={removing !== null}
        onOpenChange={(next) => {
          if (!next) {
            setRemoving(null);
            setError(null);
          }
        }}
        title={`Take ${removing?.Email ?? 'this person'} off the list?`}
        description={<>They stop being emailed about purchases. A record that they were on it is kept.</>}
        confirmLabel={remove.isPending ? 'Removing…' : 'Take them off'}
        confirmDisabled={remove.isPending}
        closeOnConfirm={false}
        onConfirm={() => {
          if (removing) remove.mutate(removing);
        }}
      >
        {error && removing && (
          <p role="alert" className="rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-darkGrey">
            {error}
          </p>
        )}
      </AlertDialog>
    </section>
  );
}
